import React from 'react'
import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  BookOpen,
  Mic,
  Palette,
  Sparkles, // Using Sparkles here for consistency and theme
  Shield,
  Sun,
  Menu,
  X,
  ChevronRight,
  Zap,
  Wand2,
  Smile,
  Users,
  ShieldCheck,
  Star,
  Target,
  // Flower, // Removed Flower icon
} from 'lucide-react'

// Images (place your images in /src/assets or update paths)
// import cloud from '../Photos/clouds2.png'
import cloud from '../../src/Photos/clouds2.png'
import talkybot from '../Photos/Talkybot.png'
import tokointro from '../components/Videos/Toko_intro.mp4'
import introvideo from '../components/Videos/Intro_Video.mp4'
import PricingAndFooter from './PricingFooter'
import FeaturesGrid from './FeaturesGrid'
import { Link } from 'react-router-dom'

// --- General Component Enhancements ---

// Theme tokens (Tailwind classes)
const theme = {
  skyToMeadow: 'from-sky-200 via-blue-100 to-green-100',
  heroText: 'text-gray-900',
  softText: 'text-gray-700',
  accent: 'text-emerald-600',
  accentWarm: 'text-orange-500',
  // SEO/Accessibility improvement:
  // Focus on a larger, more readable base font size
  baseFontSize: 'text-base sm:text-lg',
  headingBase: 'font-extrabold tracking-tight',
}

// Small interactive helper: Animated floating wrapper
const Float = ({ children, delay = 0, duration = 4 }) => (
  <motion.div
    animate={{ y: [0, -12, 0] }}
    transition={{ repeat: Infinity, duration, delay, ease: 'easeInOut' }}
    // Aria-hidden to prevent screen readers from announcing purely decorative movement
    aria-hidden="true"
    className="inline-block"
  >
    {children}
  </motion.div>
)

// Primary Button Component for consistency and accessibility
const PrimaryButton = ({ href, children, className = '' }) => (
  <motion.a
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.98 }}
    href={href}
    className={`inline-flex items-center justify-center gap-3 bg-emerald-600 text-white px-6 py-3 rounded-full shadow-lg font-semibold whitespace-nowrap transition duration-300 ${className}`}
  >
    {children}
  </motion.a>
)

// --- NAVBAR ---
const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false)

  const NavLinks = (
    <>
      <a href="#features" className="hover:text-emerald-700 transition">
        Features
      </a>
      <a href="#story" className="hover:text-emerald-700 transition">
        How It Works
      </a>
      <a href="#parents" className="hover:text-emerald-700 transition">
        Parents
      </a>
      <PrimaryButton href="#pricing" className="px-4 py-2 text-sm shadow-md">
        Get Started
      </PrimaryButton>
    </>
  )
}

// --- HERO ---n

// Assume theme, Float, PrimaryButton, and icons (Star, Mic, ShieldCheck, Users, Smile) are imported
const Hero = () => {
  const [showPopup, setShowPopup] = useState(false)

  // 1. STATE DEFINITIONS (Line 162:4 - 'isPlaying')
  const [isPlaying, setIsPlaying] = useState(false)

  // 2. VARIABLE DEFINITIONS (Line 173:20 - 'videoSource')
  const videoSource = tokointro

  const fullintro = introvideo
  // 3. FUNCTION DEFINITIONS (Line 188:18 & 193:18 - 'handlePlayClick')
  const handlePlayClick = () => {
    setIsPlaying(true)
  }

  // 4. FUNCTION DEFINITIONS (Line 167:16 - 'handleVideoEnd')
  const handleVideoEnd = () => {
    setIsPlaying(false) // go back to image + play button
  }
  // 🔹 Small reusable chip component
  const FeatureChip = ({ icon: Icon, title, color }) => (
    <motion.div
      whileHover={{
        scale: 1.05,
        boxShadow: '0 10px 20px rgba(0,0,0,0.08)',
      }}
      transition={{ type: 'spring', stiffness: 400, damping: 12 }}
      className={`flex items-center gap-2 p-3 rounded-xl shadow-md border-2 bg-white/90 backdrop-blur-sm 
        border-${color}-300`}
    >
      <Icon className={`w-5 h-5 text-${color}-600 shrink-0`} />
      <span className="font-semibold text-sm sm:text-base text-gray-800 whitespace-nowrap">
        {title}
      </span>
    </motion.div>
  )


  return (
    <section
      className={`relative flex items-center justify-center min-h-[90vh] bg-gradient-to-b ${theme.skyToMeadow} overflow-hidden`}
      aria-labelledby="hero-heading"
    >
      {/* ☁️ Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <img
          src={cloud}
          alt="Decorative background cloud graphic 1"
          className="absolute left-[-4rem] top-10 w-44 sm:w-64 opacity-60 animate-float-slow"
          loading="lazy" // Lazy load background image
        />
        <img
          src={cloud}
          alt="Decorative background cloud graphic 2"
          className="absolute right-[-4rem] top-20 w-52 sm:w-72 opacity-50 animate-float"
          loading="lazy" // Lazy load background image
        />
      </div>

      {/* 🌈 Main Layout */}
      {/* 🌈 Main Layout */}
      <div className="relative z-10 w-full max-w-7xl mx-auto flex flex-col-reverse lg:flex-row items-center justify-between gap-12 px-5 sm:px-8 md:px-12 py-16 md:py-24">
        {/* 🤖 Mascot/Media Section (Left on Desktop) - STATIC VERSION */}
        {/* 🤖 Mascot/Media Section (Left on Desktop) - STATIC VERSION */}
        <div className="lg:w-5/12 flex justify-center items-center order-2 lg:order-1 relative pt-8 lg:pt-0">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }} // Slightly refined initial animation
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative flex justify-center w-full" // Added w-full for better layout control
          >
            {isPlaying ? (
              <video
                controls
                autoPlay
                onEnded={handleVideoEnd}
                // SEO/Lazy Load Video changes
                preload="none"
                poster={talkybot} // Use the mascot image as a poster
                className="
        w-full max-w-xl md:max-w-2xl lg:max-w-3xl
        h-96 md:h-[30rem] lg:h-[36rem]
        rounded-2xl shadow-2xl ring-4 ring-blue-500/50 
        object-contain  /* <-- FIX: Ensures the whole video is seen, no cropping */
        transition-all duration-300 ease-in-out
    "
              >
                <source src={videoSource} type="video/mp4" />
              </video>
            ) : (
              // REMOVED <Float duration={4.5}> component to stop floating animation
              <div className="relative">
                <motion.img
                  src={talkybot}
                  alt="TalkyToon mascot - Click to play intro"
                  // SEO/Lazy Load Image changes
                  loading="lazy"
                  className="
                                w-full max-w-[500px] h-auto object-cover rounded-2xl shadow-2xl 
                                transition-all duration-300 ease-in-out cursor-pointer ring-4 ring-yellow-400/50
                            "
                  // REMOVED whileHover={{ scale: 1.08 }} to stop hover movement
                  onClick={handlePlayClick}
                />

                {/* ⭐ Fully Responsive Play Button (perfect position) */}
                <motion.button
                  onClick={handlePlayClick}
                  className="
                                absolute bottom-5 right-5 
                                p-4 sm:p-5 md:p-6
                                rounded-full bg-red-600 text-white 
                                shadow-2xl hover:bg-red-700 transition-colors 
                                flex items-center justify-center
                            "
                  aria-label="Play mascot intro video"
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12" // Increased play button size
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 2.503 0 3.215L7.28 20.991c-1.25.687-2.779-.217-2.779-1.643V5.653z"
                      clipRule="evenodd"
                    />
                  </svg>
                </motion.button>
              </div>
            )}
          </motion.div>
        </div>

        {/* ✨ Text Section (Right on Desktop) - Unchanged for this request */}
        <div className="lg:w-7/12 order-1 lg:order-2 text-center lg:text-left space-y-7">
          {/* 🌟 Heading */}
          <motion.h1
            id="hero-heading"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-tight text-gray-900"
          >
            <span className="block text-gray-800">
              Discover a World Where Kids
            </span>
            <span className="block bg-clip-text text-transparent bg-gradient-to-r from-emerald-500 to-blue-500">
              Talk, Imagine & Grow
            </span>

            <span className="mt-4 flex justify-center lg:justify-start items-center flex-wrap gap-2 text-gray-700 text-base sm:text-lg md:text-2xl font-medium">
              Fun, engaging, and <strong>magical</strong> moments await!
            </span>
          </motion.h1>

          {/* 📖 Subtext */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-gray-700 text-sm sm:text-base md:text-lg leading-relaxed max-w-2xl mx-auto lg:mx-0"
          >
            Say hello to <strong>TalkyToon</strong> — a playful friend who makes
            stories come alive! Every tale becomes an adventure that sparks{' '}
            <strong>curiosity</strong>, builds <strong>speech skills</strong>,
            and nurtures <strong>empathy</strong> through laughter and
            imagination.
          </motion.p>

          {/* 🌿 Core Skills */}
          <div className="pt-4 border-t border-gray-200">
            <h3 className="font-bold text-lg text-gray-800 mb-3">
              Core Skills Your Child Builds:
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 justify-center lg:justify-start">
              {/* Assuming FeatureChip component is defined elsewhere */}
              <FeatureChip icon={Mic} title="Clear Speech" color="pink" />
              <FeatureChip icon={Wand2} title="Imagination" color="purple" />
              <FeatureChip icon={Smile} title="Empathy" color="orange" />
            </div>
          </div>

          {/* 🎯 CTAs */}
          <div className="mt-8 flex flex-col sm:flex-row justify-center lg:justify-start gap-4">
            {/* Primary Action (Start Intro Video) - Main CTA styling */}
            <button
              onClick={() => setShowPopup(true)}
              className="bg-lime-400 hover:bg-lime-500 text-gray-900 py-3 px-8 text-lg rounded-full font-semibold shadow-lg transition-transform hover:scale-105"
            >
              Start Intro Video
            </button>
            {/* Secondary Action (Explore Stories) - Outline/Ghost styling */}
            <Link
              to="/storytime" // Assuming '/stories' is the path to your main content page
              className="border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 
        font-medium py-3 px-8 rounded-full text-lg transition flex items-center justify-center"
            >
              Explore Our Stories
            </Link>

            {showPopup && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-start justify-center pt-32 z-[99]"
              >
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  className="bg-white rounded-2xl p-8 shadow-2xl w-[95%] max-w-4xl relative"
                >
                  {/* CLOSE ICON */}
                  <button
                    onClick={() => {
                      console.log('Close clicked') // debug
                      setShowPopup(false)
                    }}
                    className="absolute top-2 right-6 z-50 p-2 flex items-center justify-center 
              text-gray-700 hover:text-black rounded-full hover:bg-gray-100 transition"
                    aria-label="Close video player"
                  >
                    {/* Note: X component needs to be imported, e.g., from 'lucide-react' */}
                    <X className="w-6 h-6" />
                  </button>

                  {/* VIDEO CONTENT */}
                  <div className="w-full">
                    <video
                      controls
                      autoPlay
                      onEnded={() => setShowPopup(false)}
                      // SEO/Lazy Load Video changes (for popup)
                      preload="none"
                      poster={talkybot} // Use the mascot image as a poster
                      className="w-full h-[500px] max-h-[80vh] rounded-xl object-contain"
                    >
                      <source src={fullintro} type="video/mp4" />
                    </video>
                  </div>

                  <div className="mt-4 flex justify-center">
                    {/* Keeping this empty div/margin for spacing */}
                  </div>
                </motion.div>
              </motion.div>
            )}
          </div>

          {/* 🔒 Trust Indicators */}
          <div className="mt-8 flex flex-col sm:flex-row items-center sm:items-start gap-4 pt-6 text-sm text-gray-600 border-t border-gray-200">
            <div className="flex justify-center sm:justify-start items-center gap-3">
              <ShieldCheck className="w-5 h-5 text-green-500" />
              <span>
                <strong>100% Ad-Free</strong> & Designed for Kids
              </span>

            </div>
            <div className="flex justify-center sm:justify-start items-center gap-3">
              <Users className="w-5 h-5 text-green-500" />
              <span>
                Trusted by <strong>Parents & Educators</strong>
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

// Helper component for Orbiting bubbles (Unchanged)
const OrbitBubble = ({
  icon: Icon,
  color,
  size,
  position,
  duration,
  xMove,
  yMove,
}) => {
  const sizeMap = {
    sm: 'w-10 h-10',
    md: 'w-12 h-12',
    lg: 'w-14 h-14',
  }
  const iconSize = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  }

  let animateProps = {}
  if (xMove) {
    animateProps = { x: [0, -18, 0] }
  } else if (yMove) {
    animateProps = { y: [0, -10, 0] }
  } else {
    animateProps = { rotate: 360 }
  }

  return (
    <motion.div
      aria-hidden
      className={`absolute ${position}`}
      animate={animateProps}
      transition={{
        repeat: Infinity,
        duration,
        ease: xMove || yMove ? 'easeInOut' : 'linear',
      }}
    >
      <div
        className={`${sizeMap[size]} rounded-full bg-${color}-100 flex items-center justify-center shadow-lg ring-2 ring-${color}-200 cursor-pointer hover:scale-110 transition`}
      >
        <Icon className={`${iconSize[size]} text-${color}-700`} />
      </div>
    </motion.div>
  )
}

// --- STORYTELLING SECTION (How It Works) (Unchanged) ---
const Storytelling = () => (
  <section id="story" className="py-24 px-4 sm:px-8 md:px-12 bg-white">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

        {/* Left: Interactive Preview */}
        <motion.div
          initial={{ x: -50, opacity: 0 }}
          whileInView={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, amount: 0.3 }}
          className="relative flex justify-center order-2 lg:order-1"
        >
          <div className="w-full max-w-md rounded-3xl bg-gradient-to-b from-emerald-100 to-white p-6 shadow-2xl ring-4 ring-emerald-200">

            {/* Header */}
            <h3 className="text-xl font-bold mb-4 text-emerald-800 flex items-center gap-2">
              <Zap className="w-5 h-5" /> Live Experience
              {/* The following image uses talkybot, which is likely already loaded in the hero. 
                 Since this is below the fold, lazy loading is beneficial. */}
            </h3>

            {/* TalkyBot Card */}
            <div className="bg-white rounded-xl p-4 border shadow-inner">
              <div className="flex items-center gap-3">
                <img
                  src={talkybot}
                  alt="TalkyBot mascot avatar"
                  className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-xl"
                  loading="lazy" // Lazy load for SEO
                />
                <div>
                  <div className="font-bold text-lg sm:text-xl">TOKO</div>
                  <div className="text-xs sm:text-sm text-gray-500">
                    Your playful learning buddy
                  </div>
                </div>
              </div>

              <p className="mt-4 text-sm sm:text-base md:text-base text-gray-800 border-l-4 border-blue-400 pl-3 italic">
                "Hi Mira! Shall we find a word that starts with 'F' today?"
              </p>

              {/* Interactive Buttons */}
              <div className="mt-5 space-y-2">
                <button className="w-full px-4 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm sm:text-base font-semibold hover:bg-blue-200 transition text-left shadow-sm">
                  F-L-U-T-T-E-R 🦋 (Read with me)
                </button>
                <button className="w-full px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg text-sm sm:text-base font-semibold hover:bg-yellow-200 transition text-left shadow-sm">
                  F-I-S-H 🐠 (Let's draw)
                </button>
              </div>

              <p className="mt-6 text-xs sm:text-sm text-gray-500 pt-4 border-t">
                Mira chose 'Flutter' — +5 <strong>Confidence Points</strong> for reading aloud.
              </p>
            </div>

            {/* Progress Snapshot */}
            <div className="mt-8 bg-white p-4 rounded-xl shadow-md border border-gray-200">
              <h4 className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 text-emerald-500" /> Mira's Progress Snapshot
              </h4>
              <div className="flex justify-between items-center text-xs text-gray-600 mb-1">
                <span>Next Level: Phonics Master</span>
                <span>85% Complete</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-emerald-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: '85%' }}
                />
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Tracking 4 skills: Volume, Tone, Phonics, and Fluency.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Right: Story Details */}
        <div className="order-1 lg:order-2">
          <h2 className="text-4xl sm:text-5xl lg:text-5xl font-extrabold text-gray-900">
            A Day with TalkyToon: 3 Fun Steps
          </h2>
          <p className="mt-4 text-sm sm:text-base md:text-lg text-gray-700 max-w-xl">
            Follow Mira, age 5, through a playful 10-minute session. Each activity blends fun, learning, and confidence-building.
          </p>

          {/* Steps */}
          <ol className="mt-10 space-y-6 lg:space-y-8 text-gray-700">

            {/* Step 1 */}
            <motion.li
              initial={{ x: 50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true, amount: 0.5 }}
              className="flex flex-col sm:flex-row items-start gap-4 p-4 bg-white rounded-lg shadow-md border-l-4 border-emerald-500"
            >
              <div className="text-2xl text-emerald-600 font-black sm:flex-shrink-0">1.</div>
              <div>
                <h3 className="font-bold text-lg sm:text-xl text-gray-900 flex items-center gap-2">
                  <BookOpen className="w-5 h-5" /> Interactive Story
                </h3>
                <p className="mt-1 text-sm sm:text-base">
                  A short, adaptive tale with fun choices that teach <strong>new words</strong> and spark <strong>imagination</strong>.
                </p>
              </div>
            </motion.li>

            {/* Step 2 */}
            <motion.li
              initial={{ x: 50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true, amount: 0.5 }}
              className="flex flex-col sm:flex-row items-start gap-4 p-4 bg-white rounded-lg shadow-md border-l-4 border-blue-500"
            >
              <div className="text-2xl text-blue-600 font-black sm:flex-shrink-0">2.</div>
              <div>
                <h3 className="font-bold text-lg sm:text-xl text-gray-900 flex items-center gap-2">
                  <Mic className="w-5 h-5" /> Speech & Pronunciation
                </h3>
                <p className="mt-1 text-sm sm:text-base">
                  TalkyBot encourages children to read aloud, building <strong>speech confidence</strong> and fun <strong>engagement</strong>.
                </p>
              </div>
            </motion.li>

            {/* Step 3 */}
            <motion.li
              initial={{ x: 50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true, amount: 0.5 }}
              className="flex flex-col sm:flex-row items-start gap-4 p-4 bg-white rounded-lg shadow-md border-l-4 border-orange-500"
            >
              <div className="text-2xl text-orange-600 font-black sm:flex-shrink-0">3.</div>
              <div>
                <h3 className="font-bold text-lg sm:text-xl text-gray-900 flex items-center gap-2">
                  <Palette className="w-5 h-5" /> Creative Reward Play
                </h3>
                <p className="mt-1 text-sm sm:text-base">
                  Fun drawing or coloring activities linked to the story, rewarding progress and nurturing <strong>creativity</strong>.
                </p>
              </div>
            </motion.li>
          </ol>
        </div>
      </div>
    </div>
  </section>
);

// --- PARENTS SECTION - Trust & Safety ---
const Parents = () => (
  <section
    id="parents"
    className="py-20 px-4 sm:px-6 md:px-12 bg-gradient-to-b from-green-50 to-white"
    aria-labelledby="parents-heading"
  >
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
        {/* Left Content */}
        <div className="flex-1 text-center lg:text-left order-2 lg:order-1">
          <h2
            id="parents-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight"
          >
            Your Child’s <span className="text-emerald-600">Safety</span> Comes
            First
          </h2>
          <p className="mt-4 text-sm sm:text-base md:text-lg text-gray-700 max-w-xl mx-auto lg:mx-0">
            TalkyToon provides a{' '}
            <strong>calm, private, and distraction-free</strong> learning space
            your family can trust, helping your child grow confidently.
          </p>

          <ul className="mt-8 space-y-4 text-gray-700">
            <li className="flex items-center gap-3 justify-center lg:justify-start font-semibold text-base sm:text-lg">
              <Shield className="text-emerald-600 w-5 h-5" />
              Absolutely <strong>Ad-Free</strong> Experience
            </li>
            <li className="flex items-center gap-3 justify-center lg:justify-start font-semibold text-base sm:text-lg">
              <Sun className="text-amber-500 w-5 h-5" />
              <strong>Screen-Time Controls</strong> & Play Limits
            </li>
            <li className="flex items-center gap-3 justify-center lg:justify-start font-semibold text-base sm:text-lg">
              <BookOpen className="text-blue-500 w-5 h-5" />
              Weekly, <strong>Easy-to-Read Progress</strong> Reports
            </li>
          </ul>

          {/* <div className="mt-10 flex justify-center lg:justify-start">
            <PrimaryButton
              href="#pricing"
              className="py-3 px-6 sm:px-8 text-base sm:text-lg shadow-lg"
            >
              Explore Plans for Peace of Mind
            </PrimaryButton>
          </div> */}
        </div>

        {/* Right Dashboard Preview */}
        <motion.div
          initial={{ x: 50, opacity: 0 }}
          whileInView={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, amount: 0.3 }}
          className="flex-1 order-1 lg:order-2 w-full max-w-md sm:max-w-lg mx-auto lg:mx-0"
        >
          <div className="relative rounded-3xl p-6 bg-white shadow-2xl border-4 border-yellow-200">
            <div className="text-lg sm:text-xl font-bold mb-4 text-gray-900 text-center lg:text-left">
              Parent Dashboard Snapshot
            </div>

            <div className="bg-white p-4 rounded-xl border shadow-inner">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-0">
                <div className="font-semibold text-base sm:text-lg">
                  Mira's Progress:
                </div>
                <div className="text-sm sm:text-base text-emerald-600 font-bold">
                  Week 4
                </div>
              </div>

              <div className="mt-3 space-y-2 text-sm sm:text-base">
                <div className="flex justify-between">
                  <span>New Words Learned:</span>
                  <span className="font-bold text-emerald-700">8</span>
                </div>
                <div className="flex justify-between">
                  <span>Total Practice Time:</span>
                  <span className="font-bold">70 min</span>
                </div>
                <div className="flex justify-between">
                  <span>Best Pronunciation:</span>
                  <span className="font-bold text-amber-700">
                    "Magnificent"
                  </span>
                </div>
              </div>

              <div className="mt-6 text-sm sm:text-base text-gray-500 pt-3 border-t text-center lg:text-left">
                Recommendation: Try a <strong>Space Adventure</strong> story
                next week!
              </div>
            </div>


          </div>
        </motion.div>
      </div>
    </div>
  </section>
)

// --- MAIN APP (Unchanged) ---
export default function App() {
  return (
    <div className="font-['Nunito', 'Inter', sans-serif] antialiased text-gray-900">
      <Navbar />
      <main className="bg-gradient-to-b from-sky-100 to-green-50">
        <Hero />
        <FeaturesGrid />
        <Storytelling />
        <Parents />
      </main>
    </div>
  )
}